import React, { useEffect, useState } from 'react'
import { PenTool, UserPlus, User } from 'react-feather'
import { Box, Button, Flex, HStack, Text, ListItem, UnorderedList, Tooltip, useDisclosure } from '@chakra-ui/react'
import { useGlobalContext } from 'providers/GlobalProvider'
import { useProviderCollaborator } from 'providers/CollaboratorsProvider'
import { removePlusSign } from 'utils/functions/formatNumber'
import CardFrame from 'components/common/CardFrame/CardFrame'
import IBalert from 'components/common/IBalert/IBalert'
import IbBadge from 'components/common/IbBadge'
import { useNavigate } from 'react-router-dom'
import FooterButtons from 'components/common/FooterButtons/FooterButtons'
import { saveCollaborator } from 'services/api'
import { ISaveCollaborators } from 'services/IApi.d'
import { isSuccessStatus } from 'utils/functions/validations'
import useGa from 'hooks/ga/useGa'
import CollaboratorsModal from './CollaboratorsModal'
import { ICollaborators, SuscriptionInfo } from './interfaces.d'
import { ROLES_IDS } from './constants'
import InfoCircle from 'assets/icons/infoCircle'

interface ICollaboratorsProps {
  subscriptionInfo: SuscriptionInfo,
  title: string,
}

interface IMessageErrorLimitCollaborator {
  typeMessage: number,
  titleDescription?: string,
  textMessage?: string,
}

const Collaborators: React.FunctionComponent<ICollaboratorsProps> = ({ subscriptionInfo, title }) => {
  const { isRequestSent, idEmpresa, idSuscriptor, setShowCollaboratorsModal, showCollaboratorsModal } = useGlobalContext()
  const { user, selectedCollaborators } = useProviderCollaborator()
  const navigate = useNavigate()
  const { sendDataLayer } = useGa()
  const { titleInfo, subtitleInfo, extraInfo, collaBoratorsActions, administratorActions, subscriptionType, restrictions } = subscriptionInfo
  const [collaboratorEditInfo, setCollaboratorEditInfo] = useState<ICollaborators>(null)
  const [listCollaborators, setListCollaborators] = useState<ICollaborators[]>([])
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true)
  const [showAlertError, setShowAlertError] = useState<boolean>(false)
  const [updateListCollaborators, setUpdateListCollaborators] = useState<ICollaborators>(null)
  const [vos, setVos] = useState<string>('')
  const [typeError, setTypeError] = useState<IMessageErrorLimitCollaborator>({ typeMessage: 0 })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenError, onOpen: onOpenError, onClose: onCloseError } = useDisclosure()
  const handleBack = () => navigate('/dashboard')

  const handleContinue = async () => {
    try {
      const listToSaveCollaborator: ISaveCollaborators[] = listCollaborators.map(col => ({
        apellido: col.lastName,
        codArea: col.codArea,
        codPais: removePlusSign(col.codPais),
        email: col.email,
        idEmpresa,
        esSuscriptor: col.esSuscriptor,
        idSuscriptor,
        nacionalidad: col.nacionality,
        nombre: col.name,
        numeroDocumento: col.document.replace(/-/g, ''),
        rol: col.role.map(rol => +rol),
        telefono: removePlusSign(col.phoneNumber),
        tipoDocumento: col.documentType === 'PASAPORTE' ? 'PAS' : 'Documento',
      }))
      const response: any = await saveCollaborator(listToSaveCollaborator)
      if (isSuccessStatus(response.status)) {
        sendDataLayer({
          event: 'completed_onboarding_step',
          onboarding_step: 'Cargar colaboradores',
          first_time: 'Si'
        })
        navigate('/dashboard')
        return
      }
      setTypeError({ typeMessage: 0 })
      setShowAlertError(true)
      onOpenError()
      throw Error('Error al guardar colaborador')
    } catch (error) {
      setTypeError({ typeMessage: 0 })
      setShowAlertError(true)
      onOpenError()
    }
  }

  const isMixSubscription = subscriptionType === 'mixta'
  const isPhysicSubscription = subscriptionType === 'fisica'

  const limitLegalRepresentative = 3
  const titleDescriptionLegalRepresentative = 'representantes legales'
  const textLimitLegalRepresentative = `${limitLegalRepresentative} ${titleDescriptionLegalRepresentative}`

  const limitAdministrator = isPhysicSubscription ? 10 : 7
  const titleDescriptionAdministrator = 'administradores'
  const textLimitAdministrator = `${limitAdministrator} ${titleDescriptionAdministrator}`

  const titleDescriptionCollaborator = 'colaboradores'
  const textLimitLegalRepresentativeAndAdministrator = `${limitAdministrator} ${titleDescriptionAdministrator} y ${limitLegalRepresentative} ${titleDescriptionLegalRepresentative}`

  const isRepresentative = (role: number[]) => role.some(el => el === ROLES_IDS.representative)
  const isInitialCollaborator = (document: string) => listCollaborators.some(el => el.document === document)
  const handleAddCollaborator = () => {
    setShowCollaboratorsModal(true)
    onOpen()
  }

  const validateLimitColaborator = (id: number) => {
    const isChangeRoleAdministrator = !listCollaborators?.find(item => item.id === id)?.role?.includes(ROLES_IDS.administrator) && updateListCollaborators?.role?.includes(ROLES_IDS.administrator)
    const isChangeRoleRepresentative = !listCollaborators?.find(item => item.id === id)?.role?.includes(ROLES_IDS.representative) && updateListCollaborators?.role?.includes(ROLES_IDS.representative)

    const cantAdministrator = listCollaborators?.filter(item => item.role.includes(ROLES_IDS.administrator)).length
    const cantLegalRepresentative = listCollaborators?.filter(item => item.role.includes(ROLES_IDS.representative)).length

    const changeRepresentative = isChangeRoleRepresentative ? (cantLegalRepresentative >= limitLegalRepresentative) : (cantLegalRepresentative > limitLegalRepresentative)
    const changeAdministrator = isChangeRoleAdministrator ? (cantAdministrator >= limitAdministrator) : (cantAdministrator > limitAdministrator)

    if (changeRepresentative && changeAdministrator) {
      setTypeError({
        typeMessage: 1,
        titleDescription: titleDescriptionCollaborator,
        textMessage: textLimitLegalRepresentativeAndAdministrator
      })
      setShowAlertError(true)
      onOpenError()
      return false
    }
    if (changeRepresentative) {
      setTypeError({
        typeMessage: 1,
        titleDescription: titleDescriptionLegalRepresentative,
        textMessage: textLimitLegalRepresentative
      })
      setShowAlertError(true)
      onOpenError()
      return false
    }
    if (changeAdministrator) {
      setTypeError({
        typeMessage: 1,
        titleDescription: titleDescriptionAdministrator,
        textMessage: textLimitAdministrator
      })
      setShowAlertError(true)
      onOpenError()
      return false
    }
    return true
  }

  const handleRepresentative = (collaborator: ICollaborators) => {
    if (validateLimitColaborator(collaborator.id)) {
      if (!isRepresentative(collaborator.role)) {
        setListCollaborators(
          old => old.map(
            element => element.document === collaborator.document ? { ...element, role: [...element.role, ROLES_IDS.representative] } : element
          )
        )
      } else {
        setListCollaborators(
          old => old.map(
            element => element.document === collaborator.document ? { ...element, role: element.role.filter(el => el !== ROLES_IDS.representative) } : element
          )
        )
      }
    }
  }

  const handleDelete = (document: string) => {
    const newListCollaborators = listCollaborators.filter(el => el.document !== document)
    setListCollaborators(newListCollaborators)
  }

  const handleEdit = (item: any): void => {
    const selectedCollaborator = listCollaborators.find(colaborator => item.id === colaborator.id)
    if (!selectedCollaborator) return
    setCollaboratorEditInfo(item)
    handleAddCollaborator()
  }

  const collaboratorActionTooltip = 'El banco requiere que presentes documentación en sucursal para operar con sus cuentas. Accederás a la descarga de estos documentos más adelante.'

  const ErrorPreset = [
    {
      title: 'Algo anda mal',
      text: 'En estos momentos presentamos inconvenientes en la plataforma, por favor intentá nuevamente en unos minutos',
      variant: 'sm'
    },
    {
      title: `No podés agregar más ${typeError.titleDescription}`,
      text: `Como máximo podés ingresar hasta ${typeError.textMessage}`,
      variant: 'sm'
    }
  ]

  useEffect(() => {
    const roleList = listCollaborators.map(collaborator => (collaborator.role)).flatMap(role => role)
    const representativeLegalQuantity = roleList.filter(role => role === ROLES_IDS.representative).length
    const administratorQuantity = roleList.filter(role => role === ROLES_IDS.administrator).length
    const { minAdministrators, minLegalRepresentative } = restrictions
    if (representativeLegalQuantity >= minLegalRepresentative && administratorQuantity >= minAdministrators) {
      setIsButtonDisabled(false)
    } else {
      setIsButtonDisabled(true)
    }
  }, [listCollaborators])

  useEffect(() => {
    const initialCollaborators: ICollaborators[] = []
    if (user) {
      const profile = user?.profile
      setVos(profile?.documento)
      if (selectedCollaborators.length > 0) {
        selectedCollaborators.forEach(selectedCollaborator => {
          // eslint-disable-next-line no-nested-ternary
          const documentType = selectedCollaborator?.tipoDocumento === 'PAS' ? 'PASAPORTE' : selectedCollaborator?.nacionalidad === 'AR' ? 'CUIL' : 'DOCUMENTO EXTRANJERO'
          initialCollaborators.push({
            // eslint-disable-next-line dot-notation
            id: selectedCollaborator['_id'],
            name: selectedCollaborator?.nombre,
            lastName: `${selectedCollaborator?.apellido}`,
            nacionality: selectedCollaborator?.esSuscriptor ? profile?.pais : selectedCollaborator?.nacionalidad,
            document: selectedCollaborator?.esSuscriptor ? profile?.documento : selectedCollaborator?.numeroDocumento,
            documentType,
            esSuscriptor: selectedCollaborator?.esSuscriptor,
            email: selectedCollaborator?.email?.toLowerCase(),
            role: selectedCollaborator?.rol,
            codPais: removePlusSign(selectedCollaborator.codPais), // agregado el area code
            phoneNumber: removePlusSign(selectedCollaborator?.telefono),
            codArea: selectedCollaborator.codArea,
          })
        })
      }
    }
    setListCollaborators(initialCollaborators.sort((a, b) => {
      if (a.id < b.id)
        return -1
      if (a.id > b.id)
        return 1
      return 0
    }))
  }, [user])

  useEffect(() => {
    if (updateListCollaborators === null) return

    const { id, nacionality, documentType, document, role, email, name, lastName, codPais, codArea, phoneNumber } = updateListCollaborators

    if (validateLimitColaborator(id)) {
      const newList = listCollaborators.map(item => {
        if (item.id === id) {
          return {
            ...item,
            role: role.map(rol => +rol),
            nacionality,
            documentType,
            document,
            esSuscriptor: false,
            email,
            name,
            lastName,
            codPais,
            codArea,
            phoneNumber
          }
        }
        return item
      })
      if (id) {
        setListCollaborators(newList)
      } else {
        setListCollaborators([...listCollaborators, { ...updateListCollaborators, id: listCollaborators.length + 1 }])
      }
    }

  }, [updateListCollaborators])

  return (
    <>
      <Flex flexDir='column' flex={0.70}>
        <Box textStyle='h1' mb={2}>{title}</Box>
        <Box textStyle='h5' color='grey.120' mb='24px'>{titleInfo}</Box>
        <Flex mb={2} pt='24px' borderTop='1px solid' borderTopColor='secondary.60' data-testid='actionButtons' justifyContent='space-between' pl={4}>
          <Box textStyle='h5Bold'>Colaboradores asignados</Box>
          <Button
            leftIcon={<UserPlus color={isRequestSent ? 'grey.60' : 'white'} />}
            onClick={handleAddCollaborator}
            disabled={isRequestSent}
          >
            Agregar colaboradores
          </Button>
        </Flex>
        <Flex flexDirection='column' my={4} p='16px' borderRadius='8px' bg='neutral.40'>
          <Box textStyle='labelLg'>Asigná como mínimo <strong>{subtitleInfo}</strong></Box>
          <Box textStyle='labelLg'>{extraInfo}</Box>
        </Flex>
        <Box height='35vh' overflow='auto'>
          {listCollaborators && listCollaborators.map((item, index) => (
            <CardFrame
              alignItems='center'
              data-testid={`collaborator-${index}`}
              p={2}
              pr={6}
              key={item.name}
              mb='16px'
            >
              <HStack spacing={8}>
                <Box textStyle='h6' p={4}>
                  <Text as='span' textTransform='capitalize'>{`${item.name} ${item.lastName}`}</Text>
                  <Text as='span'>{`${vos === item.document ? ' (vos)' : ''}`}</Text>
                </Box>
                {(item.role).map((role, indexRole) => (
                  <Box data-testid={`collaborator-badge-${indexRole}`} key={role}>
                    <IbBadge variant='brand-dark' color='tertiary.700' fontSize='12px' borderRadius='2xl' py={1}>
                      {+role === 1 ?
                        <><PenTool size='18px' /> Administrador</>
                        :
                        <><User size='18px' /> Representante</>
                      }
                    </IbBadge>
                  </Box>
                ))}
              </HStack>
              {!isPhysicSubscription &&
                <Box data-testid='collaborator-actions'>
                  {isInitialCollaborator(item.document) && index === 0 &&
                    <Button
                      variant={isRepresentative(item.role) ? 'link' : 'outline'}
                      borderColor='primary.500'
                      data-testid='representative-button'
                      color='primary.500'
                      onClick={() => handleRepresentative(item)}
                      disabled={isRequestSent}
                    >
                      {`${isRepresentative(item.role) ? 'No s' : 'S'}oy representante`}
                    </Button>
                  }
                </Box>
              }
              {index !== 0 &&
                <Box data-testid='collaborator-actions'>
                  <Button
                    variant='primary-text'
                    borderColor='primary.500'
                    data-testid='delete-button'
                    color='primary.500'
                    onClick={() => handleDelete(item.document)}
                    disabled={isRequestSent}
                  >
                    Quitar
                  </Button>
                  <Button
                    variant='primary-text'
                    borderColor='primary.500'
                    ml={5}
                    pl={5}
                    borderLeft='1px solid'
                    borderLeftColor='secondary.100'
                    borderRadius={0}
                    data-testid='edit-button'
                    color='primary.500'
                    onClick={() => handleEdit(item)}
                    disabled={isRequestSent}
                  >
                    Ver o modificar
                  </Button>
                </Box>
              }
            </CardFrame>
          ))}
        </Box>
      </Flex>
      <Flex flexDir='column' flex={0.30} p={10} pt={36} color='neutral.20'>
        {collaBoratorsActions &&
          <CardFrame data-testid='disclaimer-legal-representative' flexDirection='column'>
            <Flex alignItems='center' p='24px' borderBottom='1px solid' borderBottomColor='secondary.60'>
              <User size='34px' /> 
              <Text ml='12px' textStyle='h6'>
                ¿Qué harán los representantes legales o personas apoderadas?
              </Text>
            </Flex>
            <Flex flexDirection='column' p='24px'>
              <Text mb='16px' textStyle='titleSmBold' color='grey.120'>
                Confirmarán tu solicitud antes de enviarse a los bancos.
              </Text>
              {isMixSubscription &&
                <UnorderedList mb={2}>
                  {collaBoratorsActions.map(({ text, tooltip }) => (
                    <ListItem mb='2' key={text} textStyle='labelLg' color='grey.120'>
                      {text}{tooltip &&
                        <Tooltip
                          label={collaboratorActionTooltip}
                          aria-label='A tooltip'
                          placement='top'
                        >
                          <InfoCircle color='primary.500' height='20px' width='20px' mx={2} />
                        </Tooltip>
                      }
                    </ListItem>
                  ))}
                </UnorderedList>
              }
              <Box>
                <Text textStyle='bodySm' color='tertiary.700'>
                  Agregá los mismos que figuran en los bancos que elegiste para esta solicitud.
                </Text>
              </Box>
            </Flex>
          </CardFrame>
        }
        <CardFrame data-testid='disclaimer-administrator' flexDirection='column' mt='24px'>
          <Flex alignItems='center' p='24px' borderBottom='1px solid' borderBottomColor='secondary.60'>
            <PenTool size='24px' />
            <Text ml='12px' textStyle='h6'>
              ¿Qué harán los administradores?
            </Text>
          </Flex>
          <Flex flexDirection='column' p='24px'>
            <UnorderedList>
              {administratorActions.map(action => (
                <ListItem mb='2' key={action} textStyle='labelLg' color='grey.120'>
                  {action}
                </ListItem>
              ))}
            </UnorderedList>
          </Flex>
        </CardFrame>
      </Flex>
      {showAlertError &&
        <IBalert
          type='error'
          isOpen={isOpenError}
          onClose={onCloseError}
          title={ErrorPreset[typeError.typeMessage].title}
          text={ErrorPreset[typeError.typeMessage].text}
          variant={ErrorPreset[typeError.typeMessage].variant}
          buttonClose
        />
      }
      {showCollaboratorsModal && <CollaboratorsModal
        listCollaborators={listCollaborators}
        collaboratorEditInfo={collaboratorEditInfo}
        setCollaboratorEditInfo={setCollaboratorEditInfo}
        isPhysicSubscription={isPhysicSubscription}
        isOpen={isOpen}
        onClose={onClose}
        setShowModal={setShowCollaboratorsModal}
        setUpdateListCollaborators={setUpdateListCollaborators}
      />}
      <FooterButtons
        onBack={handleBack}
        onNext={() => handleContinue()}
        nextDisabled={isButtonDisabled || isRequestSent}
        backText='Volver'
        nextText='Guardar colaboradores'
      />
    </>
  )
}

export default Collaborators
